import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { Summary } from '../../widgets/Summary';
import ContentWrapper from '../../widgets/ContentWrapper';
import SpeechBubble from '../../widgets/SpeechBubble';
import Technologies from '../../widgets/Technologies';
import ImageSection from '../../widgets/ImageSection';
import ConnectStrip from '../../widgets/ConnectStrip';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import Text from '../../widgets/Text';

const fmTop = require('../../assets/images/work-mockups/fannie-mae-card-top-right.png');
const fmTopHighRes = require('../../assets/images/work-mockups/fannie-mae-card-top-right@2x.png');
const fmMain = require('../../assets/images/work-mockups/fannie-mae-mockup.png');
const fmMainHighRes = require('../../assets/images/work-mockups/fannie-mae-mockup@2x.png');
const fmBottomLeft = require('../../assets/images/work-mockups/fannie-mae-card-bottom-left.png');
const fmBottomLeftHighRes = require('../../assets/images/work-mockups/fannie-mae-card-bottom-left@2x.png');
const fmBottomRight = require('../../assets/images/work-mockups/fannie-mae-card-bottom-right.png');
const fmBottomRightHighRes = require('../../assets/images/work-mockups/fannie-mae-card-bottom-right@2x.png');

const factory = create();

export const CaseStudyFannie = factory(function CaseStudyFannie() {
	return (
		<div classes={css.root}>
			<Head
				title="Modernizing &amp; Automating Technology for Fannie Mae | SitePen"
				description="Learn more about our trusted partnership with Fannie Mae, and how we collaborated with them to modernize and automate their enterprise applications."
			/>
			<Hero background="level1BlueGreen" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Fannie Mae',
					blurb: (
						<virtual>
							<span>
								Today’s engineering teams are faced with the daunting challenge of
								translating the needs of intricate business processes into software
								solutions. SitePen’s leadership and expertise in UX design,
								application architecture and engineering allows Fannie Mae to
								deliver powerful solutions to support the needs of their demanding
								power users.
							</span>
							<Technologies
								icons={[
									'typescript',
									'angular',
									'rxjs',
									'protractor',
									'karma',
									'spring',
									'jasmine',
									'java'
								]}
							></Technologies>
						</virtual>
					)
				}}
			</Hero>
			<ContentWrapper size="small">
				<Text type="section">The Situation</Text>
				<Text type="body">
					Fannie Mae was looking to accelerate the design and development of its real
					time, data-heavy platform and receive leading industry expertise in the
					architecture, development and optimization of complex, business-critical web
					applications.
				</Text>
			</ContentWrapper>
			<ImageSection shaded paddingTop="lg" contentBias>
				{{
					title: 'The Solution',
					blurb: (
						<div classes={[css.stackedBlurb]}>
							<p>
								Fannie Mae has built a trusted partnership with the SitePen team as
								it continues to empower its users with the most resilient
								applications for their day-to-day success.
							</p>
							<p>
								For nearly a decade, SitePen has provided ongoing expertise around
								application architecture and design, and both client-side and
								server-side engineering. The SitePen team has substantial experience
								in building efficient, complex enterprise web applications and
								helped Fannie Mae:
							</p>
							<ul classes={css.bullets}>
								<li classes={css.bulletItem}>
									Solve challenges with critical applications including real-time
									scalability with very large data sets and complex business
									logic.
								</li>
								<li classes={css.bulletItem}>
									Modernize software architecture and DevOps to improve the
									reliability and efficiency of adding and releasing new features.
								</li>
								<li classes={css.bulletItem}>
									Bridge the gap that often exists between the business and
									engineering teams through the design of enterprise applications
									for advanced user scenarios.
								</li>
								<li classes={css.bulletItem}>
									Accelerate the migration of enterprise applications from legacy
									to modern software architecture based on Fannie Mae’s preferred
									architectural standards.
								</li>
							</ul>
						</div>
					),
					images: {
						main: (
							<div classes={[css.stackedMainImage]}>
								<Image
									src={fmMain}
									highResSrc={fmMainHighRes}
									shadow="light"
									responsive={{
										medium: { shadow: 'dark' }
									}}
								/>
							</div>
						),
						top: (
							<Image src={fmTop} highResSrc={fmTopHighRes} shadow="dark" size="lg" />
						),
						bottom: {
							left: (
								<Image
									src={fmBottomLeft}
									highResSrc={fmBottomLeftHighRes}
									shadow="dark"
									size="lg"
								/>
							),
							right: (
								<Image
									src={fmBottomRight}
									highResSrc={fmBottomRightHighRes}
									shadow="dark"
									size="lg"
								/>
							)
						}
					}
				}}
			</ImageSection>
			<Section type="leading">
				<ContentWrapper>
					<Summary collapse="stackReverse" imageGutter="medium" trailingBias="small">
						{{
							title: 'The Result',
							blurb:
								'SitePen collaborated with Fannie Mae teams with multiple applications and development groups for major functionality releases, application rearchitectures and redesigns, and major technology and architecture upgrades. With SitePen’s guidance and support, Fannie Mae has made significant strides in improving automated testing and other DevOps refinements. They also received coaching and mentoring to improve and sustain engineering best practices.',
							image: (
								<SpeechBubble>
									{{
										main:
											'We originally used SitePen to help develop functionality in areas where we either had no expertise or little time. Now they’re our go-to for full products.',
										subtitle1: 'Head of Digital Products',
										subtitle2: 'Fannie Mae'
									}}
								</SpeechBubble>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyFannie;
